import { pick, toUpper } from 'lodash';

import { AddressFormFeatures, AddressSuggestion, Recipient } from 'api/api';

import { bootstrapConfig } from '~src/context/config';
import { apiInstance } from '~src/global-state';

import { complementAddress } from '~address-form/utils/format';

export const COUNTRIES_WITH_EXTENDED_REGION_VALIDATION = ['US'];

type LocationResult = 'VALID' | 'INVALID';

export const suggestionService = (
  recipient: Recipient,
  country: string,
  extendedValidation: boolean,
  suggestionProvider: AddressFormFeatures['suggestion_provider']
): Promise<{ result: LocationResult; suggestion?: AddressSuggestion }> =>
  apiInstance.v4
    .validateAddressPost(bootstrapConfig.sessionId, {
      address: { ...complementAddress(recipient, country), country },
      locales: [bootstrapConfig.locale],
    })
    .then(res => {
      const {
        data: { suggestion, result },
      } = res;

      if (!suggestion?.address) {
        // We should receive the google response here from SIW backend
        // Lack of suggestion could either mean everything matches or that google didn't respond at all
        // In case of Loqate we trust the result
        return {
          result: suggestionProvider === 'loqate' ? (result as LocationResult) : 'INVALID',
          suggestion: { invalid_components: suggestion?.invalid_components },
        };
      }

      const essentialFieldNames = ['postal_code', 'city'];
      if (recipient.street_number) {
        essentialFieldNames.push('street', 'street_number');
      } else {
        essentialFieldNames.push('address_lines');
      }

      if (extendedValidation && COUNTRIES_WITH_EXTENDED_REGION_VALIDATION.includes(country)) {
        // If we are in the US and we have extended validation enabled, we need to validate the region - H&M tax purposes
        essentialFieldNames.push('region');
      }

      const suggestionWithAddressLines = complementAddress(suggestion.address, country);
      const essentialSuggestionFields = pick(suggestionWithAddressLines, essentialFieldNames);
      const essentialRecipientFields = pick(recipient, essentialFieldNames) as Record<
        string,
        string | string[]
      >;

      const invalidComponents = Object.entries(essentialSuggestionFields)
        .reduce((currentArray, [name, value]) => {
          if (name === 'address_lines') {
            if ((value as string[])[0] !== recipient.address_lines?.[0]) {
              return [...currentArray, 'ADDRESS_LINES'];
            }
            return currentArray;
          }
          if (essentialRecipientFields[name] !== value) {
            return [...currentArray, name];
          }
          return currentArray;
        }, [] as string[])
        .map(toUpper);

      return {
        result: invalidComponents.length === 0 ? 'VALID' : 'INVALID',
        suggestion: {
          invalid_components: invalidComponents,
          address: suggestionWithAddressLines,
        },
      };
    });
